import type {
  IHttpService,
  ILocationService,
  IRootScopeService,
} from 'angular';
import type ng from 'angular';

import type { OverrideMap } from 'contexts/ListingLabelContext';
import {
  DEFAULT_VALUE,
  type ListingLabelContextType,
} from 'contexts/ListingLabelContext';
import type { ListingLabelOverride } from '@bb/automaton';
import type { OrgDoc, ListingDoc } from '@bb/domain/dist/schemas';
import type { BuildJsonType } from '@bb/domain/dist/types';
import errorReporter from '../errorReporter';

import template from './view_listing.html';

type ListingViewAsset = {
  type: 'map' | 'image';
  url?: string;
  address?: string;
};

// @TODO: move to `@/app-package`
type GetViewListing = BuildJsonType<
  Omit<
    ListingDoc,
    | 'availability_history'
    | 'visibility'
    | 'pageStatus'
    | 'brokerage_remarks'
    | 'org'
  > & {
    org: Pick<OrgDoc, 'id' | 'addresses' | 'name' | 'onboardingPortalId'> & {
      customLogin: string;
    };
    imageUrls: string[];
  }
>;

type GetViewListingsResponse = { listings: GetViewListing[]; linkExpiry: Date };

class ViewListingsController implements ng.IOnInit {
  private mapVisible: boolean;

  private listingId: string;

  private dataLoaded = false;

  private error = false;

  private listings: GetViewListing[];

  private linkExpiry: Date;

  private labelOverrideMap: ListingLabelContextType['labelOverrideMap'];

  private expired: boolean;

  private customMarkers: {
    position: [number, number];
    price: string;
    id: string;
  }[];

  private date: Date;

  // eslint-disable-next-line no-useless-constructor
  constructor(
    private $http: IHttpService,
    private $location: ILocationService,
    private NgMap: angular.map.INgMap,
    private $anchorScroll: ng.IAnchorScrollService,
    private ListingViewService: any,
    private $rootScope: IRootScopeService & { labelOverrideMap: OverrideMap },
  ) {}

  goToListing = (lid: string) => {
    this.$location.hash(`lid_${lid}`);
    this.$anchorScroll();
  };

  goToMap = () => {
    this.$location.hash('map');
    this.$anchorScroll();
  };

  $onInit = () => {
    const params = this.$location.search();
    this.listingId = params.lid;
    if (params.lid) {
      this.$http
        .get<GetViewListingsResponse>(`/view/${params.lid}`)
        .then(response => {
          this.$rootScope.labelOverrideMap = DEFAULT_VALUE;
          if (response.data.listings[0]?.org.onboardingPortalId) {
            this.$http
              .get<
                ListingLabelOverride[]
              >(`/onboarding-portal/${response.data.listings[0].org.onboardingPortalId}/listing-label-override`)
              .then(res => {
                if (res.data) {
                  this.$rootScope.labelOverrideMap = res.data.reduce(
                    (acc, curr) => {
                      acc[curr.label] = {
                        ...acc[curr.label],
                        label: curr.override,
                        hidden: curr.hideLabel,
                      };
                      return acc;
                    },
                    {} as OverrideMap,
                  );
                }
              });
          }

          return response;
        })
        .then(response => {
          this.dataLoaded = true;
          if (!response.data) {
            this.error = true;
          } else {
            this.listings = response.data.listings;
            this.linkExpiry = response.data.linkExpiry;
            this.expired = new Date(this.linkExpiry) < new Date();

            const bounds = new google.maps.LatLngBounds();
            // populate markers
            this.customMarkers = [];
            this.mapVisible = false;
            this.listings = this.listings.map(
              (listing: GetViewListing & { assets: ListingViewAsset[] }) => {
                const listingWithAssets = { ...listing };
                if (listing.coords) {
                  this.mapVisible = true;
                  const latlng: [number, number] = [
                    listing.coords.coordinates[1],
                    listing.coords.coordinates[0],
                  ];
                  this.customMarkers.push({
                    position: latlng,
                    // @ts-ignore
                    price: `$${listing.list_price.formatMoney(0, '.', ',')}`,
                    id: listing.id,
                  });
                  bounds.extend(
                    new google.maps.LatLng({
                      lat: listing.coords.coordinates[1],
                      lng: listing.coords.coordinates[0],
                    }),
                  );
                }
                listingWithAssets.assets =
                  this.ListingViewService.generateAssets(
                    listing,
                    listing.imageUrls,
                  );
                return listingWithAssets;
              },
            );

            // resize map bug
            if (this.mapVisible) {
              this.NgMap.getMap({ id: 'listingMap' })
                .then(map => {
                  google.maps.event.trigger(map, 'resize');
                  map.fitBounds(bounds);
                  google.maps.event.addListenerOnce(map, 'idle', () => {
                    google.maps.event.trigger(map, 'resize');
                    map.fitBounds(bounds);
                    if (this.customMarkers.length === 1) {
                      map.setZoom(14);
                    }
                  });
                })
                .catch(err => {
                  errorReporter.error(err);
                });
            }
          }
          this.date = new Date();
        })
        .catch(err => {
          this.dataLoaded = true;
          this.error = true;
          errorReporter.error(err);
        });
    } else {
      this.dataLoaded = true;
      this.error = true;
    }
  };
}

ViewListingsController.$inject = [
  '$http',
  '$location',
  'NgMap',
  '$anchorScroll',
  'ListingViewService',
  '$rootScope',
];

angular.module('bb.external').component('bbViewListings', {
  bindings: {},
  controller: ViewListingsController,
  controllerAs: 'vm',
  template,
});
